import { useState } from "react";
import { getAbsoluteUrl } from "../../../utils/getAbsoluteUrl";

export interface Article {
  url: string;
  id: number;
  title: string;
  description: string | null;
  imageUrl: string;
}

export interface APIResult {
  totalArticles: number;
  articlesStart: number;
  articleAmount: number;
  articlePages: Article[];
}

export const useArticles = (articlesPerPage = 6) => {
  const [articles, setArticles] = useState<Article[]>([]);
  const [currentPageLoaded, setCurrentPageLoaded] = useState(0);
  const [loading, setLoading] = useState(false);
  const [hasMoreArticles, setHasMoreArticles] = useState(true);

  const getPageNumber = async (requestedPage = 1) => {
    if (currentPageLoaded < requestedPage) {
      setLoading(true);
      const startArticleNumber = currentPageLoaded * articlesPerPage;
      const result = await fetchArticles(startArticleNumber, articlesPerPage * requestedPage);

      if (!result) {
        console.error("Failed to load articles.")
        return false;
      }

      const fetchedArcticles = result.articlePages;

      const newArticles = [
        ...articles,
        ...fetchedArcticles
      ];

      setHasMoreArticles(newArticles.length < result.totalArticles);
      setCurrentPageLoaded(requestedPage);
      setArticles(newArticles);
      setLoading(false);
    }
  }

  const fetchArticles = async (start: number, amount: number): Promise<APIResult | null> => {
    try {
      // Use absolute URL base to account for if the URL doesn't end with a forward slash
      const urlBase = getAbsoluteUrl();

      const urlToFetch = `${urlBase}GetArticles?start=${start}&amount=${amount}`;
      const response = await fetch(urlToFetch);
      const json = await response.json();

      return {
        totalArticles: json.totalArticles,
        articlesStart: json.articleStart,
        articleAmount: json.articleAmount,
        articlePages: json.articlePages
      };
    } catch (error) {
      console.error(error)
      return null;
    }
  }

  return { articles, getPageNumber, currentPageLoaded, loading, hasMoreArticles };
}
